import React, { useEffect, useRef } from 'react';
import { formatDateLabel } from './constants';
import AllDayToggle from '../planning/AllDayToggle';
import useEventCategories from '../planning/useEventCategories';
import { useTranslation } from 'react-i18next';

export default function EventModal({
  open,
  mode,
  title,
  date,
  endDate,
  start,
  end,
  allDay,
  category,
  timeError,
  onChange,
  onClose,
  onSubmit,
  onDelete,
}) {
  const { t } = useTranslation('planning');
  const { t: tCommon } = useTranslation('common');
  const titleRef = useRef(null);
  const { codes, labels } = useEventCategories();

  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => titleRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const subtitle =
    endDate && endDate !== date
      ? `${formatDateLabel(date)} – ${formatDateLabel(endDate)}`
      : date
        ? formatDateLabel(date)
        : '';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4 month-event-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-sm bg-surface p-4 rounded-xl shadow-[0_8px_24px_rgba(18,65,145,0.18)] border border-border month-event-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-event-heading"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-1.5 h-5 rounded-full bg-nokia-blue shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h2 id="create-event-heading" className="text-sm font-bold text-primary leading-snug">
                {mode === 'edit' ? t('editEvent') : t('newEvent')}
              </h2>
              <button
                type="button"
                className="p-0.5 text-text-secondary hover:text-primary rounded transition-colors shrink-0"
                aria-label={tCommon('close')}
                onClick={onClose}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
            <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>
          </div>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.();
          }}
        >
          <div>
            <label htmlFor="month-event-title" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('eventTitleOptional')} <span className="font-normal opacity-70">{t('optionalSuffix')}</span>
            </label>
            <input
              ref={titleRef}
              id="month-event-title"
              type="text"
              maxLength={80}
              placeholder={t('addTitle')}
              value={title}
              onChange={(e) => onChange?.({ title: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="month-event-date" className="block text-[11px] font-semibold text-text-secondary mb-1">
                Start day
              </label>
              <input
                id="month-event-date"
                type="date"
                required
                value={date}
                onChange={(e) => onChange?.({ date: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
              />
            </div>
            <div>
              <label htmlFor="month-event-end-date" className="block text-[11px] font-semibold text-text-secondary mb-1">
                End day
              </label>
              <input
                id="month-event-end-date"
                type="date"
                required
                value={endDate || date}
                min={date || undefined}
                onChange={(e) => onChange?.({ endDate: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
              />
            </div>
          </div>
          <AllDayToggle
            id="month-event-all-day"
            checked={!!allDay}
            onChange={(checked) => onChange?.({ allDay: checked })}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="month-event-start" className="block text-[11px] font-semibold text-text-secondary mb-1">
                Start time
              </label>
              <input
                id="month-event-start"
                type="time"
                required
                disabled={!!allDay}
                value={allDay ? '00:00' : start}
                onChange={(e) => onChange?.({ start: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label htmlFor="month-event-end" className="block text-[11px] font-semibold text-text-secondary mb-1">
                End time
              </label>
              <input
                id="month-event-end"
                type="time"
                required
                disabled={!!allDay}
                value={allDay ? '23:59' : end}
                onChange={(e) => onChange?.({ end: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          {timeError ? (
            <p className="text-[11px] text-red-600 font-medium">{t('endMustBeAfterStart')}</p>
          ) : null}
          <div>
            <span className="block text-[11px] font-semibold text-text-secondary mb-1.5">{t('category')}</span>
            <div className="category-radio-grid" role="radiogroup" aria-label={t('eventCategory')}>
              {codes.map((cat) => (
                <label key={cat}>
                  <input
                    type="radio"
                    name="month-event-category"
                    value={cat}
                    checked={category === cat}
                    onChange={() => onChange?.({ category: cat })}
                  />
                  <span>{labels[cat] || cat}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            {mode === 'edit' ? (
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                onClick={onDelete}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center justify-end gap-2 ml-auto">
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-main hover:bg-background-light rounded-lg transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white text-xs font-medium rounded-lg active:scale-[0.98] transition-all"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
